import { Injectable } from '@angular/core';
import { RemoteLoggerService } from './remote-logger.service';
import { GeocodingService } from './geocoding.service';
import { OrderLocationSelectorStore } from '../stores/order-location-selector.store';
import { NavigatorGeolocationService } from './navigator-geolocation.service';
import { OrderStore } from '../stores/order.store';
import { first, map } from 'rxjs/operators';
import { OrderLocationGroupContract } from '../models/order-location-group.model';
import { ThemeService } from './theme.service';
import { RxUtil } from 'utility/rx.util';

@Injectable({
  providedIn: 'root'
})
export class OrderLocationSelectorService {
  constructor(
    private orderStore: OrderStore,
    private orderLocationSelectorStore: OrderLocationSelectorStore,
    private geocodingService: GeocodingService,
    private navigatorGeolocationService: NavigatorGeolocationService,
    private logger: RemoteLoggerService,
    private themeService: ThemeService
  ) {}

  async initializeTheme() {
    // initialize the theme with the first location in the default target list
    const firstTarget = await RxUtil.takeOne(
      this.orderStore.orderLocationGroupTargets$.pipe(
        first((targets) => targets.length > 0),
        map((targets) => targets[0])
      )
    );

    if (firstTarget.locationTheme) {
      this.themeService.setupTheme(firstTarget.locationTheme);
    }
  }

  toggleSearchCurrentLocationLoading(isLoading: boolean) {
    this.orderLocationSelectorStore.isSearchingCurrentLocation = isLoading;
  }

  toggleSearchClosestLocationLoading(isLoading: boolean) {
    this.orderLocationSelectorStore.isSearchingClosestLocations = isLoading;
  }

  async searchPlaceNames(searchString: string): Promise<void> {
    try {
      const result = await this.geocodingService.searchPlaceNames(searchString);
      this.orderLocationSelectorStore.placeNameOptions = result
        ? result.features
        : [];
    } catch (e) {
      this.logger.capture(e);
    } finally {
      this.toggleSearchCurrentLocationLoading(false);
    }
  }

  loadCurrentPositionState() {
    this.navigatorGeolocationService.updateCurrentPosition();
  }

  isGeolocationDenied(): Promise<boolean> {
    return this.navigatorGeolocationService.isGeolocationDenied();
  }

  loadCurrentPosition() {
    this.navigatorGeolocationService.loadCurrentPosition();
  }

  async loadClosestLocationsByPlace(
    companyDomain: string,
    searchString: string
  ): Promise<void> {
    try {
      this.toggleSearchClosestLocationLoading(true);
      const orderLocationGroup =
        await this.geocodingService.getClosestLocationsByPlace(
          companyDomain,
          searchString ?? ''
        );
      this.orderStore.setV2OrderLocationGroupTargets(
        orderLocationGroup.locationTargets
      );
    } catch (e) {
      this.logger.capture(e);
    } finally {
      this.toggleSearchClosestLocationLoading(false);
    }
  }

  async loadClosestLocationsByLatLong(
    companyDomain: string,
    lat: number,
    long: number
  ): Promise<void> {
    try {
      this.toggleSearchClosestLocationLoading(true);
      const orderLocationGroup =
        await this.geocodingService.getClosestLocationsByLatLong(
          companyDomain,
          lat,
          long
        );

      this.orderStore.setV2OrderLocationGroupTargets(
        orderLocationGroup.locationTargets
      );
    } catch (e) {
      this.logger.capture(e);
    } finally {
      this.toggleSearchClosestLocationLoading(false);
    }
  }

  //default list (ip lookup)
  async getClosestLocationsByDistance(
    companyDomain: string
  ): Promise<OrderLocationGroupContract | null> {
    try {
      return await this.geocodingService.getClosestLocationsByDistance(
        companyDomain
      );
    } catch {
      return null;
    }
  }

  async loadClosestLocationsByDistance(companyDomain: string): Promise<void> {
    try {
      this.toggleSearchClosestLocationLoading(true);
      const orderLocationGroup =
        await this.geocodingService.getClosestLocationsByDistance(
          companyDomain
        );
      this.orderStore.setV2OrderLocationGroupTargets(
        orderLocationGroup.locationTargets
      );
    } catch (e) {
      this.logger.capture(e);
    } finally {
      this.toggleSearchClosestLocationLoading(false);
    }
  }
}
